import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { IMG } from "@/constants/bbAssets";
import { Reveal, SunMark, WaveLine } from "@/components/beachbreak/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GETAWAY_IMG =
  "https://static.prod-images.emergentagent.com/jobs/322e4ca0-c0f6-4f4a-92be-f850cf034ff2/images/db7d9bf102afa9910d0d1d261bd8c9b6ccd13f011be5df57ebd10b7fb9f48f52.jpeg";
const PARTY_IMG =
  "https://static.prod-images.emergentagent.com/jobs/322e4ca0-c0f6-4f4a-92be-f850cf034ff2/images/0a6b73e7fc11567e3ceb66be49139871372643cc4fffbc55d2384b709187b87c.jpeg";
const WATERFALL_IMG =
  "https://static.prod-images.emergentagent.com/jobs/322e4ca0-c0f6-4f4a-92be-f850cf034ff2/images/09f64a3e16696d5a88743f2b70fcae355c935602903cf2c3e21bb11d03befe95.jpeg";

const AGE_OPTIONS = ["18–20", "21–24", "25–34", "35+"];
const BACKGROUND_OPTIONS = ["Current Player", "Former Player", "Recreational Player", "New to Beach"];

const EASE = [0.22, 1, 0.36, 1];

const scrollToForm = () => {
  const el = document.getElementById("getaway-form");
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const ChipGroup = ({ label, options, value, onChange, testPrefix, required = false }) => (
  <div>
    <p className="font-mono text-[10px] uppercase tracking-[0.18em] mb-2">
      {label} {required ? "*" : ""}
    </p>
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          data-testid={`${testPrefix}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          onClick={() => onChange(opt)}
          className={`font-mono text-[10px] uppercase tracking-[0.14em] px-4 py-2 rounded-full border transition-colors duration-200 ${
            value === opt ? "bg-deep text-cream border-deep" : "border-deep/25 text-deep hover:border-deep"
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  </div>
);

const PILLARS = [
  {
    n: "01",
    title: "Play",
    desc: "Every day brings a new way to play, with organized 6s, coed, women's, and men's tournaments throughout the trip.",
  },
  { n: "02", title: "Explore", desc: "Beach days, local activities, food, excursions, and destination experiences." },
  { n: "03", title: "Connect", desc: "Meet players from everywhere through nightly events, player parties, group adventures, and a vacation built around the volleyball community." },
];

export const GetawayPage = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    age_range: "",
    background: "",
  });
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) return setError("Please add your name.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) return setError("Please add a valid email.");
    if (!form.age_range) return setError("Please choose an age range.");
    if (!form.background) return setError("Please choose your volleyball background.");

    try {
      setSending(true);
      const res = await axiosPost(form);
      if (res.data?.ok) setDone(true);
      else setError(res.data?.error || "Something went wrong. Please try again.");
    } catch (err) {
      setError(err.response?.data?.error || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const axiosPost = (payload) =>
    fetch(`${API}/getaway-interest`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then((r) => r.json().then((d) => ({ data: d, ok: undefined })));

  return (
    <div className="bg-cream text-deep">
      {/* header */}
      <header className="bg-cream/90 backdrop-blur-md border-b border-deep/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 md:h-[76px] flex items-center justify-between gap-4">
          <a href="/" data-testid="getaway-logo-home" className="flex items-center gap-3">
            <img src={IMG.logo} alt="Beach Break Sand Volleyball" className="h-10 md:h-12 w-auto" />
          </a>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.2em] text-deep/60">
              Part of Beach Break Volleyball
            </span>
            <button
              data-testid="getaway-header-cta"
              onClick={scrollToForm}
              className="bg-deep text-cream font-mono text-[11px] uppercase tracking-[0.18em] px-5 py-2.5 rounded-full hover:bg-sun hover:text-deep transition-colors duration-200"
            >
              Join the Interest List
            </button>
          </div>
        </div>
      </header>

      <main>
        {/* hero */}
        <section className="relative bg-deep text-cream overflow-hidden">
          <SunMark className="absolute -top-8 -right-10 w-72 h-48 text-cream/[0.06] rotate-6 pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <motion.p
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="font-mono text-[11px] uppercase tracking-[0.24em] text-sun flex items-center gap-2"
              >
                <span className="w-8 h-px bg-sun" /> For adults • Coming 2028
              </motion.p>
              <h1 className="mt-5 font-display uppercase leading-[0.95] text-[clamp(3rem,8vw,5.5rem)]">
                <span className="block overflow-hidden">
                  <motion.span className="block" initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.15, ease: EASE }}>
                    The Getaway<span className="text-sun">.</span>
                  </motion.span>
                </span>
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.4 }}
                className="mt-4 font-serifit italic text-2xl md:text-3xl text-cream/90"
              >
                Your next beach vacation has a net.
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.5 }}
                className="mt-5 text-base md:text-lg text-cream/80 leading-relaxed max-w-md"
              >
                The Getaway brings volleyball players together for destination trips built around
                beach volleyball, travel, and community.
              </motion.p>
              <motion.button
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.6 }}
                data-testid="getaway-hero-cta"
                onClick={scrollToForm}
                className="mt-8 inline-flex items-center gap-2 bg-cream text-deep font-mono text-[11px] uppercase tracking-[0.18em] px-7 py-4 rounded-full hover:bg-sun transition-colors duration-200"
              >
                Join the Interest List
                <ArrowUpRight className="w-4 h-4" />
              </motion.button>
            </div>
            <div className="lg:col-span-6">
              <div className="photo-frame max-w-md mx-auto lg:ml-auto rotate-2" data-testid="getaway-hero-photo-frame">
                <img src={GETAWAY_IMG} alt="Adults playing beach volleyball together at golden hour" className="w-full aspect-[4/3] object-cover" data-testid="getaway-hero-photo" />
                <p className="pt-2.5 font-mono text-[9px] md:text-[10px] uppercase tracking-[0.2em] text-deep/70">
                  Volleyball. Travel. Community.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* what is it */}
        <section className="py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-5 md:px-8">
            <div className="grid lg:grid-cols-12 gap-10">
              <div className="lg:col-span-5">
                <Reveal>
                  <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-deep/70 flex items-center gap-2">
                    <span className="w-8 h-px bg-sun" /> The concept
                  </p>
                  <h2 className="mt-4 font-display uppercase text-deep leading-[0.95] text-[clamp(2.4rem,5.5vw,4rem)]">
                    What is The Getaway?
                  </h2>
                  <p className="mt-6 text-base md:text-lg text-deep-soft leading-relaxed">
                    A social, active beach vacation for adults who love volleyball. Guests can play,
                    explore the destination, meet new people, and enjoy an organized trip without
                    planning everything themselves.
                  </p>
                  <WaveLine className="mt-6 w-36 h-5 text-aqua" />
                  <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-deep/60">
                    One trip. Three things that matter.
                  </p>
                </Reveal>
              </div>
              <div className="lg:col-span-6 lg:col-start-7">
                {PILLARS.map((p, i) => (
                  <Reveal key={p.n} delay={0.1 * i}>
                    <div data-testid={`getaway-pillar-${i}`} className="group border-t-2 border-deep/15 py-7 hover:border-sun transition-colors duration-300">
                      <p className="font-mono text-xs tracking-[0.2em] text-deep/50 group-hover:text-sun transition-colors duration-300">{p.n}</p>
                      <h3 className="mt-2 font-display uppercase text-deep text-2xl md:text-3xl">{p.title}</h3>
                      <p className="mt-2 text-sm md:text-base text-deep-soft leading-relaxed">{p.desc}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>

            <Reveal delay={0.2}>
              <div className="mt-16 grid sm:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto">
                <div className="photo-frame -rotate-1" data-testid="getaway-party-photo-frame">
                  <img
                    src={PARTY_IMG}
                    alt="Players smiling together at a beach bar party"
                    className="w-full aspect-[4/3] object-cover"
                    data-testid="getaway-party-photo"
                  />
                  <p className="pt-2.5 font-mono text-[9px] md:text-[10px] uppercase tracking-[0.2em] text-deep/70">
                    Player parties + nightly events
                  </p>
                </div>
                <div className="photo-frame rotate-1 sm:mt-10" data-testid="getaway-excursion-photo-frame">
                  <img
                    src={WATERFALL_IMG}
                    alt="Friends laughing at a waterfall on a Getaway excursion"
                    className="w-full aspect-[4/3] object-cover"
                    data-testid="getaway-excursion-photo"
                  />
                  <p className="pt-2.5 font-mono text-[9px] md:text-[10px] uppercase tracking-[0.2em] text-deep/70">
                    Waterfall hikes + excursions
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* upcoming destination */}
        <section className="relative bg-deep text-cream py-20 md:py-28 overflow-hidden border-b border-cream/10">
          <SunMark className="absolute top-6 right-6 w-40 h-28 text-cream/[0.06] rotate-12 pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-5 md:px-8 text-center">
            <Reveal>
              <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-sun flex items-center justify-center gap-2">
                <span className="w-8 h-px bg-sun" /> Upcoming destination <span className="w-8 h-px bg-sun" />
              </p>
              <h2 className="mt-5 font-display uppercase leading-[0.92]">
                <span className="block text-[clamp(2.8rem,9vw,6.5rem)]">Costa Rica</span>
                <span className="block text-[clamp(2.8rem,9vw,6.5rem)] text-outline-cream">2028</span>
              </h2>
              <p className="mt-6 text-base md:text-lg text-cream/80 leading-relaxed max-w-xl mx-auto">
                The first Beach Break Getaway is officially in the works. Join the early interest
                list to hear updates, help shape the experience, and be first in line when exact
                dates, pricing, and signup details land.
              </p>
              <WaveLine className="mt-6 w-40 h-5 text-aqua mx-auto" />
              <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.22em] text-cream/60">
                Interest list hears first.
              </p>
              <button
                data-testid="getaway-interested-cta"
                onClick={scrollToForm}
                className="mt-8 inline-flex items-center gap-2 bg-sun text-deep font-mono text-[11px] uppercase tracking-[0.18em] px-7 py-4 rounded-full hover:bg-cream transition-colors duration-200"
              >
                I'm Interested
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </Reveal>
          </div>
        </section>

        {/* form */}
        <section id="getaway-form" className="relative bg-cream text-deep py-20 md:py-28 overflow-hidden">
          <p className="absolute bottom-2 left-0 w-full font-display uppercase text-[clamp(2.6rem,10vw,7.5rem)] leading-none text-outline text-center pointer-events-none select-none whitespace-nowrap opacity-40">
            The Getaway
          </p>
          <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-deep/70 flex items-center gap-2">
                  <span className="w-8 h-px bg-sun" /> Early interest list
                </p>
                <h2 className="mt-4 font-display uppercase leading-[0.95] text-[clamp(2.4rem,5.5vw,4rem)]">
                  Join The Getaway Interest List.
                </h2>
                <p className="mt-6 text-base md:text-lg text-deep-soft leading-relaxed max-w-md">
                  Be first to hear about destinations, dates, and upcoming trips.
                </p>
                <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.22em] text-deep/60">
                  Good Vibes × Volleyball
                </p>
              </Reveal>
            </div>

            <div className="lg:col-span-6 lg:col-start-7">
              <Reveal delay={0.1}>
                {done ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.45 }}
                    data-testid="getaway-success"
                    className="bg-tint/60 text-deep rounded-2xl p-10 text-center border border-deep/10"
                  >
                    <motion.div initial={{ scale: 0, rotate: -30 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 200, damping: 14, delay: 0.15 }} className="inline-block text-sun">
                      <SunMark className="w-16 h-12" />
                    </motion.div>
                    <h3 className="mt-5 font-display uppercase text-3xl md:text-4xl">You're on the list!</h3>
                    <p className="mt-4 text-sm md:text-base text-deep-soft leading-relaxed max-w-sm mx-auto">
                      Thanks, {form.name.trim().split(" ")[0]}! We'll keep you posted as The Getaway
                      takes shape — and your input helps shape where we go first.
                    </p>
                    <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-deep/60">
                      Volleyball. Travel. Community.
                    </p>
                  </motion.div>
                ) : (
                  <form data-testid="getaway-interest-form" onSubmit={submit} className="bg-white text-deep rounded-2xl p-6 md:p-8 shadow-2xl border border-deep/10" noValidate>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="font-mono text-[10px] uppercase tracking-[0.18em] block mb-1.5" htmlFor="g-name">Name *</label>
                        <input id="g-name" data-testid="getaway-input-name" className="bb-field" placeholder="Your name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                      </div>
                      <div>
                        <label className="font-mono text-[10px] uppercase tracking-[0.18em] block mb-1.5" htmlFor="g-email">Email *</label>
                        <input id="g-email" data-testid="getaway-input-email" className="bb-field" type="email" placeholder="you@email.com" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                      </div>
                      <div>
                        <label className="font-mono text-[10px] uppercase tracking-[0.18em] block mb-1.5" htmlFor="g-phone">Phone</label>
                        <input id="g-phone" data-testid="getaway-input-phone" className="bb-field" type="tel" placeholder="Optional" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
                      </div>
                    </div>

                    <div className="mt-6 space-y-6">
                      <ChipGroup label="Age Range" options={AGE_OPTIONS} value={form.age_range} onChange={(v) => setForm((f) => ({ ...f, age_range: v }))} testPrefix="getaway-age" required />
                      <ChipGroup label="Volleyball Background" options={BACKGROUND_OPTIONS} value={form.background} onChange={(v) => setForm((f) => ({ ...f, background: v }))} testPrefix="getaway-bg" required />
                    </div>

                    {error && (
                      <p role="alert" data-testid="getaway-form-error" className="mt-4 text-sm font-semibold text-[#B4552A] bg-sun/15 border border-sun/40 rounded-lg px-4 py-2.5">
                        {error}
                      </p>
                    )}

                    <button
                      data-testid="getaway-submit-button"
                      type="submit"
                      disabled={sending}
                      className="mt-6 w-full bg-deep text-cream font-mono text-xs uppercase tracking-[0.18em] px-6 py-4 rounded-full hover:bg-sun hover:text-deep transition-colors duration-200 disabled:opacity-60"
                    >
                      {sending ? "Sending…" : "Join The Getaway"}
                    </button>
                  </form>
                )}
              </Reveal>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-deep text-cream border-t border-cream/10 pb-10">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-12">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div className="inline-block bg-cream rounded-xl p-2.5">
              <img src={IMG.logo} alt="Beach Break Sand Volleyball" className="h-10 w-auto rounded" data-testid="getaway-footer-logo" />
            </div>
            <a href="/" data-testid="getaway-back-home" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/80 hover:text-sun transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Beach Break home
            </a>
          </div>
          <p className="mt-10 font-display uppercase text-center leading-none text-[clamp(1.6rem,6vw,4.5rem)] text-outline-cream select-none">
            Volleyball. Travel. Community.
          </p>
          <div className="mt-8 pt-5 border-t border-cream/10 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-cream/50">The Getaway — part of Beach Break Volleyball</p>
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-cream/50">Tampa, Florida • Costa Rica 2028</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GetawayPage;
